import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	Contact,
	ContactKyc,
	ContactNote,
	CreateContactBody,
	CreateContactNoteBody,
	ListContactsParams,
	ListContactsResponse,
	UpdateContactBody,
	UpdateContactStatusBody,
	UpsertContactKycBody,
} from './types/contact'

export const contactsApi = createApi({
	reducerPath: 'contactsApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Contact', 'ContactNote', 'ContactKyc'],
	endpoints: (builder) => ({
		listContacts: builder.query<ListContactsResponse, ListContactsParams | void>({
			query: (params) => ({ url: '/crm/contacts', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.items.map((c) => ({ type: 'Contact' as const, id: c.id })),
							{ type: 'Contact' as const, id: 'LIST' },
						]
					: [{ type: 'Contact' as const, id: 'LIST' }],
		}),

		getContact: builder.query<Contact | null, string>({
			query: (id) => `/crm/contacts/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'Contact', id }],
		}),

		createContact: builder.mutation<Contact | null, CreateContactBody>({
			query: (body) => ({ url: '/crm/contacts', method: 'POST', body }),
			invalidatesTags: [{ type: 'Contact', id: 'LIST' }],
		}),

		updateContact: builder.mutation<Contact | null, { id: string; body: UpdateContactBody }>({
			query: ({ id, body }) => ({ url: `/crm/contacts/${id}`, method: 'PATCH', body }),
			// Optimistically flip the mobile-access toggle in the cached contact
			// so the switch responds instantly; the patch is undone on failure
			// (e.g. 400 when enabling access for a contact without an email).
			async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
				if (body.mobileAccessEnabled === undefined) return
				const next = body.mobileAccessEnabled
				const patch = dispatch(
					contactsApi.util.updateQueryData('getContact', id, (draft) => {
						if (draft) draft.mobileAccessEnabled = next
					}),
				)
				try {
					await queryFulfilled
				} catch {
					patch.undo()
				}
			},
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Contact', id },
				{ type: 'Contact', id: 'LIST' },
			],
		}),

		/**
		 * Sets the reliability score (Положительный / Нейтральный / Отрицательный)
		 * on a past tenant. Wired to the «Изменить статус» dropdown on the past-
		 * tenants list and detail header. Backend gap — this hook hits a stub
		 * endpoint until the backend lands the column + RPC.
		 */
		updateContactStatus: builder.mutation<Contact | null, { id: string; body: UpdateContactStatusBody }>({
			query: ({ id, body }) => ({ url: `/crm/contacts/${id}/status`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Contact', id },
				{ type: 'Contact', id: 'LIST' },
			],
		}),

		deleteContact: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/crm/contacts/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'Contact', id },
				{ type: 'Contact', id: 'LIST' },
			],
		}),

		// ── Notes ───────────────────────────────────────────

		listContactNotes: builder.query<{ items: ContactNote[] }, string>({
			query: (contactId) => `/crm/contacts/${contactId}/notes`,
			providesTags: (_r, _e, contactId) => [{ type: 'ContactNote', id: contactId }],
		}),

		addContactNote: builder.mutation<
			ContactNote | null,
			{ contactId: string; body: CreateContactNoteBody }
		>({
			query: ({ contactId, body }) => ({
				url: `/crm/contacts/${contactId}/notes`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { contactId }) => [{ type: 'ContactNote', id: contactId }],
		}),

		deleteContactNote: builder.mutation<OkResponse, { contactId: string; noteId: string }>({
			query: ({ contactId, noteId }) => ({
				url: `/crm/contacts/${contactId}/notes/${noteId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_r, _e, { contactId }) => [{ type: 'ContactNote', id: contactId }],
		}),

		// ── KYC ─────────────────────────────────────────────

		getContactKyc: builder.query<ContactKyc | null, string>({
			query: (contactId) => `/crm/contacts/${contactId}/kyc`,
			providesTags: (_r, _e, contactId) => [{ type: 'ContactKyc', id: contactId }],
		}),

		upsertContactKyc: builder.mutation<
			ContactKyc | null,
			{ contactId: string; body: UpsertContactKycBody }
		>({
			query: ({ contactId, body }) => ({
				url: `/crm/contacts/${contactId}/kyc`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: (_r, _e, { contactId }) => [{ type: 'ContactKyc', id: contactId }],
		}),
	}),
})

export const {
	useListContactsQuery,
	useGetContactQuery,
	useCreateContactMutation,
	useUpdateContactMutation,
	useUpdateContactStatusMutation,
	useDeleteContactMutation,
	useListContactNotesQuery,
	useAddContactNoteMutation,
	useDeleteContactNoteMutation,
	useGetContactKycQuery,
	useUpsertContactKycMutation,
} = contactsApi
